import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { StandardEvents } from '@shopify/events';
import { isQuarterHourTimeValue } from '@theme/utilities';

/**
 * Helium fill option radios + optional weight checkbox (family code 536) on the product page.
 * Morphs on variant change; exposes selected fill/weight variants for batch add-to-cart.
 *
 * @typedef {object} ProductFillOptionsRefs
 * @property {HTMLInputElement} [selfFillInput]
 * @property {HTMLInputElement} [heliumFillInput]
 * @property {HTMLInputElement} [weightCheckbox]
 * @property {HTMLInputElement} [pickupDateInput]
 * @property {HTMLInputElement} [pickupTimeInput]
 * @property {HTMLInputElement} [pickupDatetimeValue]
 *
 * @extends {Component<ProductFillOptionsRefs>}
 */
class ProductFillOptions extends Component {
  connectedCallback() {
    super.connectedCallback();

    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#syncAcceleratedCheckout();
    this.#syncPickupVisibility();
    this.#syncProductFormSelection();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#clearProductFormSelection();
  }

  /**
   * @returns {string | null} Helium fill variant id, or null for self-fill / hidden
   */
  getSelectedFillVariantId() {
    if (this.hasAttribute('hidden')) return null;

    const checked = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input.product-fill-options__input[type="radio"]:checked')
    );

    if (!checked) return null;

    const fillVariantId = (checked.getAttribute('data-fill-variant-id') || checked.value || '').trim();
    if (!fillVariantId) return null;

    return fillVariantId;
  }

  /**
   * @returns {string | null} Weight variant id when checkbox is checked
   */
  getSelectedWeightVariantId() {
    if (this.hasAttribute('hidden')) return null;

    const checkbox =
      this.refs.weightCheckbox instanceof HTMLInputElement
        ? this.refs.weightCheckbox
        : /** @type {HTMLInputElement | null} */ (
            this.querySelector('input.product-fill-options__input--weight[type="checkbox"]')
          );

    if (!(checkbox instanceof HTMLInputElement) || !checkbox.checked || checkbox.disabled) {
      return null;
    }

    const weightVariantId = (checkbox.getAttribute('data-weight-variant-id') || checkbox.value || '').trim();
    if (!weightVariantId) return null;

    return weightVariantId;
  }

  /**
   * Balloon pack size for unit helium SKUs (1 / 12 / 50). Pack-priced helium variants use 1.
   * @returns {number}
   */
  getHeliumPackUnits() {
    const raw = Number(this.dataset.heliumPackUnits);
    if (!Number.isFinite(raw) || raw < 1) return 1;
    return Math.floor(raw);
  }

  /**
   * @returns {boolean}
   */
  isHeliumSelected() {
    return Boolean(this.getSelectedFillVariantId());
  }

  /**
   * @returns {boolean}
   */
  isWeightSelected() {
    return Boolean(this.getSelectedWeightVariantId());
  }

  handleFillChange = () => {
    this.#syncAcceleratedCheckout();
    this.#syncPickupVisibility();
    this.#syncProductFormSelection();
  };

  handlePickupDatetimeChange = () => {
    const { pickupDateInput, pickupTimeInput } = this.refs;
    if (pickupDateInput instanceof HTMLInputElement) pickupDateInput.setCustomValidity('');
    if (pickupTimeInput instanceof HTMLInputElement && isQuarterHourTimeValue(pickupTimeInput.value)) {
      pickupTimeInput.setCustomValidity('');
    }
    this.#syncPickupDatetimeValue();
    this.#syncProductFormSelection();
  };

  /**
   * Combined pickup value `DD-MM-YYYY HH:mm` when helium is selected.
   * @returns {string | null}
   */
  getPickupDatetime() {
    if (!this.isHeliumSelected()) return null;

    this.#syncPickupDatetimeValue();
    const combined = this.#getPickupDatetimeInput();
    if (!(combined instanceof HTMLInputElement)) return null;

    const value = combined.value.trim();
    return value || null;
  }

  /**
   * Focuses date/time and shows the same incomplete message as ballooncreations.
   * @returns {boolean}
   */
  reportPickupValidity() {
    this.#syncPickupVisibility();
    this.#syncPickupDatetimeValue();

    const dateInput = this.#getPickupDateInput();
    const timeInput = this.#getPickupTimeInput();
    if (!(dateInput instanceof HTMLInputElement) || !(timeInput instanceof HTMLInputElement)) {
      return false;
    }

    const pickup = this.querySelector('[data-helium-pickup]');
    const message =
      (pickup instanceof HTMLElement && pickup.dataset.incompleteMessage) ||
      'Please choose both a date and a time.';

    const dateFilled = Boolean(dateInput.value);
    const timeFilled = Boolean(timeInput.value);

    if (timeFilled && !isQuarterHourTimeValue(timeInput.value)) {
      const picker = timeInput.closest('quarter-hour-time');
      if (picker && typeof picker.showNotice === 'function') picker.showNotice();
      timeInput.focus();
      return false;
    }

    const focusTarget = !dateFilled ? dateInput : timeInput;

    focusTarget.setCustomValidity(message);
    focusTarget.reportValidity?.();
    focusTarget.focus();
    return false;
  }

  /**
   * Mirror current selection onto the matching product-form so ATC can read it
   * even if custom-element methods are not available at click time.
   */
  #syncProductFormSelection() {
    const form = this.#getProductForm();
    if (!form) return;

    const fillVariantId = this.getSelectedFillVariantId();
    const weightVariantId = this.getSelectedWeightVariantId();
    const packUnits = this.getHeliumPackUnits();
    const pickupDatetime = this.getPickupDatetime();

    if (fillVariantId) {
      form.dataset.selectedFillVariantId = fillVariantId;
    } else {
      delete form.dataset.selectedFillVariantId;
    }

    if (weightVariantId) {
      form.dataset.selectedWeightVariantId = weightVariantId;
    } else {
      delete form.dataset.selectedWeightVariantId;
    }

    form.dataset.selectedHeliumPackUnits = String(packUnits);

    if (pickupDatetime) {
      form.dataset.selectedHeliumPickupDatetime = pickupDatetime;
    } else {
      delete form.dataset.selectedHeliumPickupDatetime;
    }
  }

  #clearProductFormSelection() {
    const form = this.#getProductForm();
    if (!form) return;

    delete form.dataset.selectedFillVariantId;
    delete form.dataset.selectedWeightVariantId;
    delete form.dataset.selectedHeliumPackUnits;
    delete form.dataset.selectedHeliumPickupDatetime;
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId ? String(this.dataset.productId) : '';
    const section = this.closest('.shopify-section, dialog') || document;

    if (productId) {
      const match = section.querySelector(`product-form-component[data-product-id="${productId}"]`);
      if (match instanceof HTMLElement) return match;
    }

    const fallback = section.querySelector('product-form-component');
    return fallback instanceof HTMLElement ? fallback : null;
  }

  /**
   * @param {import('@shopify/events').ProductSelectEvent} event
   */
  #handleProductSelect = async (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    try {
      const { detail } = await event.promise;
      if (!detail?.html) return;

      const { html, newProduct, productId } = detail;

      if (newProduct) {
        this.dataset.productId = newProduct.id;
      } else if (productId && productId !== this.dataset.productId) {
        return;
      }

      const newFillOptions = html.querySelector('product-fill-options');
      if (!newFillOptions) {
        this.hidden = true;
        this.innerHTML = '';
        this.#syncAcceleratedCheckout();
        this.#clearProductFormSelection();
        return;
      }

      const keepHeliumSelected = this.isHeliumSelected();
      const keepWeightSelected = this.isWeightSelected();
      const keepPickupDate = this.#getPickupDateInput()?.value || '';
      const keepPickupTime = this.#getPickupTimeInput()?.value || '';

      morph(this, newFillOptions, { childrenOnly: true });
      this.hidden = newFillOptions.hasAttribute('hidden');
      this.dataset.variantId = newFillOptions.dataset.variantId || '';
      this.dataset.heliumVariantId = newFillOptions.dataset.heliumVariantId || '';
      this.dataset.heliumPackUnits = newFillOptions.dataset.heliumPackUnits || '1';
      this.dataset.weightVariantId = newFillOptions.dataset.weightVariantId || '';
      this.dataset.showFillOptions = newFillOptions.dataset.showFillOptions || '';

      if (keepHeliumSelected) {
        const heliumInput = this.refs.heliumFillInput;
        if (heliumInput instanceof HTMLInputElement && !heliumInput.disabled) {
          heliumInput.checked = true;
        }
      }

      const weightCheckbox =
        this.refs.weightCheckbox instanceof HTMLInputElement
          ? this.refs.weightCheckbox
          : /** @type {HTMLInputElement | null} */ (
              this.querySelector('input.product-fill-options__input--weight[type="checkbox"]')
            );
      if (weightCheckbox instanceof HTMLInputElement && !weightCheckbox.disabled) {
        weightCheckbox.checked = keepWeightSelected;
      }

      const pickupDateInput = this.#getPickupDateInput();
      const pickupTimeInput = this.#getPickupTimeInput();
      if (keepHeliumSelected && pickupDateInput instanceof HTMLInputElement && keepPickupDate) {
        pickupDateInput.value = keepPickupDate;
      }
      if (keepHeliumSelected && pickupTimeInput instanceof HTMLInputElement && keepPickupTime) {
        pickupTimeInput.value = keepPickupTime;
      }

      this.#syncAcceleratedCheckout();
      this.#syncPickupVisibility();
      this.#syncProductFormSelection();
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.warn('[product-fill-options] Event promise rejected:', error);
      }
    }
  };

  #syncPickupVisibility() {
    const pickup = this.querySelector('[data-helium-pickup]');
    if (!(pickup instanceof HTMLElement)) return;

    const heliumSelected = this.isHeliumSelected();
    pickup.hidden = !heliumSelected;

    const dateInput = this.#getPickupDateInput();
    const timeInput = this.#getPickupTimeInput();
    const combined = this.#getPickupDatetimeInput();

    for (const input of [dateInput, timeInput, combined]) {
      if (!(input instanceof HTMLInputElement)) continue;
      input.disabled = !heliumSelected;
      input.required = heliumSelected && input.type !== 'hidden';
      if (!heliumSelected) {
        input.value = '';
        input.setCustomValidity('');
      }
    }

    if (heliumSelected) {
      this.#syncPickupDatetimeValue();
    }
  }

  #syncPickupDatetimeValue() {
    const dateInput = this.#getPickupDateInput();
    const timeInput = this.#getPickupTimeInput();
    const combined = this.#getPickupDatetimeInput();
    if (!(combined instanceof HTMLInputElement)) return;
    if (!(dateInput instanceof HTMLInputElement) || !(timeInput instanceof HTMLInputElement)) return;

    const dateValue = dateInput.value.trim();
    const timeValue = timeInput.value.trim();

    if (!dateValue || !timeValue || !isQuarterHourTimeValue(timeValue)) {
      combined.value = '';
      return;
    }

    const parts = dateValue.split('-');
    if (parts.length !== 3) {
      combined.value = '';
      return;
    }

    const [year, month, day] = parts;
    combined.value = `${day}-${month}-${year} ${timeValue}`;
  }

  /**
   * @returns {HTMLInputElement | null}
   */
  #getPickupDateInput() {
    if (this.refs.pickupDateInput instanceof HTMLInputElement) return this.refs.pickupDateInput;
    return this.querySelector('input[type="date"][ref="pickupDateInput"]');
  }

  /**
   * @returns {HTMLInputElement | null}
   */
  #getPickupTimeInput() {
    if (this.refs.pickupTimeInput instanceof HTMLInputElement) return this.refs.pickupTimeInput;
    return this.querySelector('.quarter-hour-time__input[ref="pickupTimeInput"]');
  }

  /**
   * @returns {HTMLInputElement | null}
   */
  #getPickupDatetimeInput() {
    if (this.refs.pickupDatetimeValue instanceof HTMLInputElement) return this.refs.pickupDatetimeValue;
    return this.querySelector('[data-helium-pickup-value]');
  }

  #syncAcceleratedCheckout() {
    const section = this.closest('.shopify-section, dialog');
    if (!section) return;

    const accelerated = section.querySelector('.accelerated-checkout-block');
    if (!(accelerated instanceof HTMLElement)) return;

    if (this.isHeliumSelected()) {
      accelerated.setAttribute('hidden', '');
      accelerated.dataset.hiddenForHeliumFill = 'true';
      return;
    }

    if (accelerated.dataset.hiddenForHeliumFill !== 'true') return;

    delete accelerated.dataset.hiddenForHeliumFill;

    const addButton = section.querySelector('product-form-component [ref="addToCartButton"]');
    const soldOut = addButton instanceof HTMLButtonElement && addButton.disabled;
    if (!soldOut) {
      accelerated.removeAttribute('hidden');
    }
  }
}

if (!customElements.get('product-fill-options')) {
  customElements.define('product-fill-options', ProductFillOptions);
}
